/* eslint-disable max-classes-per-file */
import { gql } from '@apollo/client/core';

import {
  AiInvolvementStatus,
  Consent,
  Country,
  DealLockStatus,
  DealProduct,
  DealType,
  DocumentInfo,
  DocumentProgressStatus,
  FollowUp,
  Maybe,
  Notification,
  NotificationSubscriber,
  OriginalLessee,
  ReferralSource,
  Tag,
} from './generated/graphql';

import { Option } from '../components/shared/types';
import { DealDates } from '../types/dealDates';
import { User } from '../types/user';

import { Car } from './carGql';
import { ConditionReport } from './conditionReportGql';
import { Cobuyer, Customer } from './customerGql';
import { DealContact } from './dealContactGql';
import { ReferralSourceFragment } from './dealReferralSourceGql';
import { DealStates } from './dealStatesGql';
import { FinancialInfoAcquisition } from './financialInfoAcquisitionGql';
import { FinancialInfo } from './financialInfoGql';
import { Note } from './noteGql';
import { Pod } from './podGql';

export type AiInvolvement = typeof AiInvolvementStatus[keyof typeof AiInvolvementStatus];

export enum DealSourceEnum {
  CallIn = 'call-in',
  D2D = 'd2d',
  Web = 'web',
  Outbound = 'outbound',
}

export const DealSourceLabelMap: {
  [key in DealSourceEnum]: string;
} = {
  [DealSourceEnum.CallIn]: 'Call-In',
  [DealSourceEnum.D2D]: 'D2D',
  [DealSourceEnum.Web]: 'Web',
  [DealSourceEnum.Outbound]: 'Outbound',
};

export const dealSourceOptions: Option[] = Object.values(DealSourceEnum).map((value) => ({
  label: DealSourceLabelMap[value],
  value,
}));

// This needs to stay in sync with the variable of the same name on the backend 👀
export enum DealStateEnum {
  // Advisors/Financial Specialists
  Estimate = 'estimate',
  Floor = 'floor',
  SoftClose = 'soft_close',
  StructuringInProgress = 'structuring_in_progress',
  Structuring = 'structuring',
  Closing = 'closing',
  Closed = 'closed',

  // Funding Clerks
  SentForSignatures = 'signatures',
  Signed = 'signed',
  Funded = 'funded',

  // Title Clerks
  SendPayoff = 'send_payoff',
  WaitingForTitle = 'waiting_for_title', // Now called Create Packet
  TitleReceived = 'title_received', // Now called Ready for Clerk
  SentToProcessor = 'sent_to_processor',

  // Logistics Coordinators
  ReadyForPickup = 'ready_for_pickup',
  PaidOff = 'paid_off',
  AtAuction = 'at_auction',
  Sold = 'sold',
  TitleSent = 'title_sent',

  // Dead Deals
  Booted = 'booted',
  Finalized = 'finalized',
}

export const DealStateLabelMap: Record<DealStateEnum, string> = {
  // Setters/Closers
  [DealStateEnum.Estimate]: 'New Estimates',
  [DealStateEnum.Floor]: 'On The Floor',
  [DealStateEnum.SoftClose]: 'Soft Close',
  [DealStateEnum.StructuringInProgress]: 'Structuring in Progress',
  [DealStateEnum.Structuring]: 'Structuring',
  [DealStateEnum.Closing]: 'Closing',
  [DealStateEnum.Closed]: 'Closed',

  // Funding/Accounting Clerks
  [DealStateEnum.SentForSignatures]: 'Sent For Signatures',
  [DealStateEnum.Signed]: 'Signed',
  [DealStateEnum.Funded]: 'Funded',

  // Title Clerks
  [DealStateEnum.SendPayoff]: 'Send Payoff',
  [DealStateEnum.WaitingForTitle]: 'Waiting For Title',
  [DealStateEnum.TitleReceived]: 'Title Received',
  [DealStateEnum.SentToProcessor]: 'Sent To Processor/DMV',

  // Logistics Coordinators - All deprecated
  [DealStateEnum.ReadyForPickup]: 'Ready For Pickup',
  [DealStateEnum.PaidOff]: 'Paid Off',
  [DealStateEnum.AtAuction]: 'At Auction',
  [DealStateEnum.Sold]: 'Sold',
  [DealStateEnum.TitleSent]: 'Title Sent',

  // Dead Deals
  [DealStateEnum.Booted]: 'Booted',
  [DealStateEnum.Finalized]: 'Finalized',
};

export const DealTypeLabelMap: Record<DealType, string> = {
  [DealType.Marketplace]: 'Marketplace',
  [DealType.Buyout]: 'Buyout',
  [DealType.Refi]: 'Refi',
  [DealType.Acquisition]: 'Acquisition',
};

export const dealTypeOptions: Option[] = Object.values(DealType).map((value) => ({
  label: DealTypeLabelMap[value],
  value,
}));

export enum BootReasonsEnum {
  BadContactInfo = 'Bad contact info',
  CashBuyer = 'Cash buyer',
  CustomerDoesNotQualify = 'Customer does not qualify',
  CustomerDoesNotWantToGiveInformation = 'Customer does not want to give information to proceed',
  CustomerWaitingEndOfLease = 'Customer waiting until end of lease',
  DirectPayState = 'Direct pay state',
  DocFeeTooHigh = 'Doc fee too high',
  DuplicateDeal = 'Duplicate deal',
  ExtendedLease = 'Extended Lease',
  LienholderNotApproved = 'Lienholder not approved',
  ManufacturerNotApproved = 'Manufacturer not approved',
  NoResponse = 'No response',
  PaymentOnApprovalTooHigh = 'Payment on approval too high',
  PayoffAmountTooHigh = 'Payoff amount too high for financing (car is too new)',
  QuoteTooHigh = 'Quote too high (monthly payment)',
  RateTooHigh = 'Rate too high',
  BoughtOutThroughDifferentSource = 'Bought out through different source',
  SalesTaxIssue = 'Sales Tax Issue',
  ThoughtWeWereManufacturer = 'Thought we were manufacturer',
  TurnedVehicleBackInToDealership = 'Turned vehicle back in to dealership',
  VehicleDoesNotQualify = 'Vehicle does not qualify for our financing (mileage too high/financing too low)',
  Unwound = 'Deal was unwound',
  Other = 'Other (please leave reason in notes)',
}

export class BootReason {
  reason: BootReasonsEnum;
  comment: string;
  author_id?: string;

  constructor(reason: BootReasonsEnum, comment = '', author_id = '') {
    this.reason = reason;
    this.comment = comment;
    this.author_id = author_id;
  }
}

export class Deal {
  id?: number;
  duplicated_from_deal_id?: number;
  type?: DealType;
  source?: string;
  state: DealStateEnum;
  country?: Country;
  setter_id?: string;
  closer_id?: string;
  closer2_id?: string;
  second_turn_financial_specialist_id?: string;
  structuring_manager_id?: string;
  funding_clerk_id?: string;
  created_at?: string;
  creation_date_tz?: string;
  creation_date_utc?: string;
  set_date?: string;
  approved_date?: string;
  completion_date_utc?: string;
  completion_date_tz?: string;
  customer: Customer;
  cobuyer?: Customer | null;
  consents?: Consent[];
  contact?: DealContact | null;
  second_contact?: DealContact | null;
  original_lessee?: OriginalLessee | null;
  car: Car;
  financial_info: FinancialInfo;
  financial_info_acquisition: FinancialInfoAcquisition;
  setter?: User;
  closer?: User;
  closer2?: User;
  structuring_manager?: User;
  funding_clerk?: User;
  lane_arrival_utc?: string;
  default_sort_date?: string;
  notes: Note[];
  paperwork_type?: string;
  condition_report?: ConditionReport;
  request_boot: boolean;
  boot_reason?: BootReason;
  document_info?: DocumentInfo;
  roi_document_info?: DocumentInfo;
  deal_states: DealStates[];
  deal_dates?: DealDates;
  fs_pod_id?: string;
  fs_pod?: Pod;
  pod_id?: string;
  pod?: Pod;
  finalized_date?: string;
  sold_date?: string;
  tags?: Tag[];
  titling_pod_id?: string;
  titling_pod?: Pod;
  title_clerk_id?: string;
  title_clerk?: User;
  title_clerk2_id?: string;
  title_clerk2?: User;
  temporary_registration_tags_user_id?: string;
  temporary_registration_tags_user?: User;
  notifications?: Notification[];
  follow_up: Maybe<FollowUp>;
  deal_products?: DealProduct[];

  has_funding_jacket?: boolean;
  document_progress_status?: DocumentProgressStatus;
  r1_contract_validation_errors_object?: { errors: string[] };
  r1_contract_validation_warnings_object?: { warnings: string[] };
  r1_contract_worksheet_created_date_utc?: string;
  r1_contract_manually_validated?: boolean;

  signing_on_com?: boolean;
  called_by_financial_specialist?: string;
  assigned_sms_phone_number?: string;
  needs_electronic_signature_verification?: boolean;
  sent_to_netsuite?: boolean;
  ai_involvement: AiInvolvement;
  lock_status?: DealLockStatus | null;
  selected_package_slot_index?: number | null;

  referral_source?: ReferralSource;
  notification_subscribers?: NotificationSubscriber[];

  // eslint-disable-next-line no-undef, @typescript-eslint/no-explicit-any
  [x: string]: any;

  constructor() {
    this.type = DealType.Buyout;
    this.source = DealSourceEnum.CallIn;
    this.state = DealStateEnum.Estimate;
    this.customer = new Customer();
    this.cobuyer = new Cobuyer();
    this.car = new Car();
    this.financial_info = new FinancialInfo();
    this.financial_info_acquisition = new FinancialInfoAcquisition();
    this.notes = [];
    this.request_boot = false;
    this.deal_states = [];
    this.needs_electronic_signature_verification = false;
    this.sent_to_netsuite = false;
    this.ai_involvement = AiInvolvementStatus.None;
    this.lock_status = null;
  }
}

export class CleanDeal {
  id?: number;
  type?: DealType;
  source?: string;
  state: DealStateEnum;
  setter_id?: string;
  closer_id?: string;
  closer2_id?: string;
  second_turn_financial_specialist_id?: string;
  funding_clerk_id?: string;
  created_at?: string;
  creation_date_tz?: string;
  completion_date_utc?: string;
  completion_date_tz?: string;
  car: Car;
  financial_info: FinancialInfo;
  financial_info_acquisition: FinancialInfoAcquisition;
  lane_arrival_utc?: string;
  default_sort_date?: string;
  notes: Note[];
  paperwork_type?: string;
  condition_report?: ConditionReport;
  request_boot: boolean;
  boot_reason?: BootReason;
  document_info?: DocumentInfo;
  roi_document_info?: DocumentInfo;
  deal_states: DealStates[];
  deal_dates?: DealDates;
  pod?: Pod;
  finalized_date?: string;
  sold_date?: string;
  ai_involvement: AiInvolvement;
  lock_status?: DealLockStatus | null;
  deal_products?: DealProduct[];

  document_progress_status?: DocumentProgressStatus;
  r1_contract_validation_errors_object?: { errors: string[] };
  r1_contract_validation_warnings_object?: { warnings: string[] };
  r1_contract_worksheet_created_date_utc?: string;
  r1_contract_manually_validated?: boolean;

  signing_on_com?: boolean;
  called_by_financial_specialist?: string;
  assigned_sms_phone_number?: string;
  needs_electronic_signature_verification?: boolean;
  sent_to_netsuite?: boolean;

  constructor(deal: Deal) {
    this.id = deal.id;
    this.type = deal.type;
    this.source = deal.source;
    this.state = deal.state;
    this.setter_id = deal.setter_id;
    this.closer_id = deal.closer_id;
    this.closer2_id = deal.closer2_id;
    this.second_turn_financial_specialist_id = deal.second_turn_financial_specialist_id;
    this.funding_clerk_id = deal.funding_clerk_id;
    this.creation_date_tz = deal.creation_date_tz;
    this.created_at = deal.created_at;
    this.completion_date_tz = deal.completion_date_tz;
    this.completion_date_utc = deal.completion_date_utc;
    this.car = deal.car;
    this.financial_info = deal.financial_info;
    this.financial_info_acquisition = deal.financial_info_acquisition;
    this.lane_arrival_utc = deal.lane_arrival_utc;
    this.default_sort_date = deal.default_sort_date;
    this.notes = deal.notes;
    this.paperwork_type = deal.paperwork_type;
    this.condition_report = deal.condition_report;
    this.request_boot = deal.request_boot;
    this.boot_reason = deal.boot_reason;
    this.document_info = deal.document_info;
    this.roi_document_info = deal.roi_document_info;
    this.deal_states = deal.deal_states;
    this.pod = deal.pod;
    this.finalized_date = deal.finalized_date;
    this.sold_date = deal.sold_date;
    this.deal_dates = deal.deal_dates;

    this.document_progress_status = deal.document_progress_status;
    this.r1_contract_validation_errors_object = deal.r1_contract_validation_errors_object;
    this.r1_contract_validation_warnings_object = deal.r1_contract_validation_warnings_object;
    this.r1_contract_worksheet_created_date_utc = deal.r1_contract_worksheet_created_date_utc;
    this.r1_contract_manually_validated = deal.r1_contract_manually_validated;
    this.called_by_financial_specialist = deal.called_by_financial_specialist;
    this.needs_electronic_signature_verification = deal.needs_electronic_signature_verification;
    this.sent_to_netsuite = deal.sent_to_netsuite;
    this.ai_involvement = deal.ai_involvement;
    this.lock_status = deal.lock_status;
  }
}

export const dealPodAndUserIdKeys: (keyof Deal)[] = [
  'pod_id',
  'setter_id',
  'closer_id',
  'closer2_id',
  'second_turn_financial_specialist_id',
  'funding_clerk_id',
  'titling_pod_id',
  'title_clerk_id',
  'title_clerk2_id',
];

// TODO: replace snippets with fragments.
// SNIPPETS
const addressSnippet = `
  id
  address_line
  address_line_2
  zip
  city
  state
  county
  country
  address_type
  residence_type
  years_at_home
  months_at_home
  monthly_payment
  moved_states
`;

const employmentSnippet = `
  id
  status
  name
  job_title
  phone_number
  years_at_job
  months_at_job
  gross_income
  pay_frequency
`;

const customerSnippet = `
  id
  first_name
  middle_name
  last_name
  name_suffix
  phone_number
  home_phone_number
  email
  ssn_id
  has_full_ssn
  has_itin
  dob
  address {
    ${addressSnippet}
  }
  prev_address {
    ${addressSnippet}
  }
  mailing_address {
    ${addressSnippet}
  }
  employment {
    ${employmentSnippet}
  }
  prev_employment {
    ${employmentSnippet}
  }
  proof_of_insurance {
    id
    company_name
    policy_number
    expires
  }
  lives_with_other_buyer
  marital_status
  prequalification {
    id
    input {
      first_name
      last_name
      zip
      gross_income
      pay_frequency
      vehicle_payoff
      lending_book_value
      money_down
      income_sources
    }
    output {
      errors
    }
    credit_score
    ltv
    payment_to_income
    dti
    customer_id
    credit_score_tier
  }
  auth0_id
  secure_link {
    id
    expiration_date
  }
`;

const payoffSnippet = `
  id
  lienholder_name
  lienholder_slug
  lender_name
  account_number
  vehicle_payoff
  good_through_date
  lease_term
  remaining_payments
  msrp
  residual_percentage
  sales_price
  cap_reduction
  money_down
  money_factor
  termination_fees
  cap_cost
  residual_amount
  estimated_payoff
  old_lease_payment
  sales_tax_from_payoff
  payoff_includes_sales_tax
  sales_tax_from_payoff_entered_manually
  verification_status
  next_payment_date
  user_entered_total_payoff
  maturity_date
`;

const kbbSelectedOptionsSnippet = `
  id
  name
  type
  lendingOptionPrice
  retailOptionPrice
  removed
`;

// Every stored field of cars.cbb_valuation, selected whole so the record can be sent back
// through the upserts without losing fields. graphql-tag-pluck only resolves template
// variables declared in the same file, so each document file keeps its own copy.
const cbbValuationSnippet = `
  uvc
  trim_name
  vehicle_name
  all_options {
    uoc
    name
    clean
    xclean
    avg
    rough
    auto
  }
  selected_options {
    uoc
    name
    clean
  }
  valuation_date
  data_freq
  inputs {
    kilometres
    province
  }
  lending {
    adjusted
    base
    mileage_adjustment
    option_adjustment
    regional_adjustment
  }
  retail {
    adjusted
    base
    mileage_adjustment
    option_adjustment
    regional_adjustment
  }
`;

const carSnippet = `
  id
  license_plate_number
  license_plate_state
  vin
  year
  make
  model
  mileage
  mileage_unit
  color
  book_value
  retail_book_value
  jdp_adjusted_clean_trade
  jdp_adjusted_clean_retail
  vehicle_type
  fuel_type
  kbb_trim_name
  mmr
  registration_expiration
  odometer_status
  kbb_selected_options_object {
    selected_options {
      ${kbbSelectedOptionsSnippet}
    }
  }
  cbb_valuation {
    ${cbbValuationSnippet}
  }
  payoff {
    ${payoffSnippet}
  }
`;

const assignedUsersSnippet = `
  pod_id
  pod {
    id
    name
    color
  }
  fs_pod_id
  fs_pod {
    id
    name
    color
  }
  setter_id
  setter {
    id
    name
    pods {
      id
    }
  }
  closer_id
  closer {
    id
    name
    pods {
      id
    }
  }
  closer2_id
  closer2 {
    id
    name
    pods {
      id
    }
  }
  second_turn_financial_specialist_id
  second_turn_financial_specialist {
    id
    name
    pods {
      id
    }
  }
  structuring_manager_id
  structuring_manager {
    id
    name
    pods {
      id
    }
  }
  funding_clerk_id
  funding_clerk {
    id
    name
    pods {
      id
    }
  }
`;

const titleUsersSnippet = `
  titling_pod_id
  titling_pod {
    id
    color
    name
    leads {
      id
      name
    }
  }
  title_clerk_id
  title_clerk {
    id
    name
    deleted_at
  }
  title_clerk2_id
  title_clerk2 {
    id
    name
    deleted_at
  }
`;

const dealStatesSnippet = `
  id
  deal_id
  state
  updated_date_utc
  user_id
  user {
    id
    name
  }
`;

const dealDatesSnippet = `
  id
  deal_id
  dates
  custom_dates
`;

const dealsSnippet = `
  id
  type
  source
  state
  created_at
  creation_date_utc
  creation_date_tz
  completion_date_utc
  lane_arrival_utc
  needs_electronic_signature_verification
  paperwork_type
  request_boot
  boot_reason
  lock_status
  car {
    ${carSnippet}
  }
  customer {
    ${customerSnippet}
  }
 ${assignedUsersSnippet}
 ${titleUsersSnippet}
  notifications {
    # TODO: use NotificationFragment when migrating to .graphql. Keep in sync in the meantime.
    id
    message
    customer_id
    deal_contact_id
    original_lessee_id
    created_at
    users {
      id
      user_id
      seen_at
    }
  }
  financial_info {
    id
    bank
    amount_financed
    processor
    title_registration_option
  }
  deal_dates {
    ${dealDatesSnippet}
  }
  tags {
    id
    display_name
    color
    is_dashboard_visible
    is_active
    badge_variant
  }
`;

const titlingExportDealsSnippet = `
  id
  type
  source
  state
  car {
    id
    year
    make
    model
    mileage
    vin
    mmr
    payoff {
      id
      lienholder_name
      lienholder_slug
      lender_name
      vehicle_payoff
      good_through_date
      verification_status
      next_payment_date
    }
  }
  customer {
    id
    first_name
    middle_name
    last_name
    address {
      ${addressSnippet}
    }
  }
  financial_info {
    id
    bank
    amount_financed
    money_down
    bank_fees
    base_tax_amount
    warranty_tax_amount
    title_registration_option
    title_fee
    new_registration_fee
    registration_transfer_fee
    doc_fee
  }
  deal_dates {
    ${dealDatesSnippet}
  }
  notes {
    id
    text
    created_at
    creation_date_tz
    author {
      name
    }
  }
  ${titleUsersSnippet}
`;

const financialInfoSnippet = `
  id
  deal_id
  money_down
  bank_fees
  base_tax_amount
  warranty_tax_amount
  title_fee
  new_registration_fee
  registration_transfer_fee
  doc_fee
  sales_tax_rate
  days_to_payment
  first_payment_date
  sell_rate
  buy_rate
  term
  bank
  payment
  amount_financed
  profit
  commissionable_profit
  setter_commission
  closer_commission
  option_type
  plate_transfer
  title_registration_option
  processor
  tt_transaction_id
  buyer_not_lessee
  reserve
  actual_reserve
  user_entered_reserve
  selected_credit_decision_id
  selected_credit_decision {
    id
    tier
  }
  credit_card_payment_amount_limit
  quick_notes
  needs_temporary_registration_tags
  new_lienholder {
    name
    provider_lender_id
    address
    city
    state
    zip
  }
`;

const dealDetailSnippet = `
  id
  duplicated_from_deal_id
  type
  source
  state
  country
  set_date
  approved_date
  completion_date_utc
  completion_date_tz
  needs_electronic_signature_verification
  paperwork_type
  request_boot
  boot_reason
  has_funding_jacket
  document_progress_status
  sales_visibility
  import_type
  called_by_financial_specialist
  assigned_sms_phone_number
  sent_to_netsuite
  lock_status
  selected_dealer_id
  selected_package_slot_index
  r1_contract_validation_errors_object {
    errors
  }
  r1_contract_validation_warnings_object {
    warnings
  }
  r1_contract_manually_validated
  signing_on_com
  consents {
    id
    type
  }
  ${assignedUsersSnippet}
  ${titleUsersSnippet}
  car {
    ${carSnippet}
  }
  customer {
    ${customerSnippet}
  }
  cobuyer {
    ${customerSnippet}
    relation_to_buyer
  }
  contact {
    id
    relation_to_buyer
    first_name
    middle_name
    last_name
    phone_number
    email
  }
  second_contact {
    id
    relation_to_buyer
    first_name
    middle_name
    last_name
    phone_number
    email
  }
  original_lessee {
    id
    first_name
    last_name
    phone_number
    email
    address_line
    ssn_id
    city
    state
    zip
    county
}
  financial_info {
    ${financialInfoSnippet}
  }
  condition_report {
    id
    deal_id
    accidents
    tires
    exterior
    interior
    smoked_in
    lights_on_dash
    overall_condition
    score
  }
  document_info {
    id
    adobe_agreement_id
  }
  roi_document_info {
    id
    adobe_agreement_id
  }
  deal_states {
    ${dealStatesSnippet}
  }
  deal_dates {
    ${dealDatesSnippet}
  }
  notifications {
    # TODO: use NotificationFragment when migrating to .graphql. Keep in sync in the meantime.
    id
    message
    customer_id
    deal_contact_id
    original_lessee_id
    created_at
    users {
      id
      user_id
      seen_at
    }
  }
  referral_source {
    ...ReferralSourceFragment
  }
  tags {
    id
    slug
    color
    display_name
    is_active
    badge_variant
  }
    notification_subscribers {
      id
      subscriber_id
    }
  `;

// QUERIES
export const titlingDashboardDealsQuery = gql`
  query titlingDashboardDeals(
    $sources: [String]!
    $types: [DealType]!
    $assignedToDeletedUsersFilter: Boolean
    $podIds: [ID]
    $page: Int
    $itemsPerPage: Int
    $filters: FilterType
    $sorting: SortingType
    $showActiveTags: Boolean
  ) {
    titlingDashboardDeals(
      sources: $sources
      types: $types
      assignedToDeletedUsersFilter: $assignedToDeletedUsersFilter
      podIds: $podIds
      page: $page
      itemsPerPage: $itemsPerPage
      filters: $filters
      sorting: $sorting
      showActiveTags: $showActiveTags
    ) {
      deals {
        ${dealsSnippet}
        setter {
          id
        }
        closer {
          id
        }
        closer2 {
          id
        }
      }
      totalRecords
      dealsMaxAge
    }
  }
`;

export const exportDealsQuery = gql`
  query titlingDashboardDeals(
    $sources: [String]!,
    $types: [DealType]!,
    $podIds: [ID], $page: Int,
    $itemsPerPage: Int,
    $filters: FilterType,
    $sorting: SortingType
    $includeTitleNotes: Boolean
  ) {
    titlingDashboardDeals(
      sources: $sources,
      types: $types,
      podIds: $podIds,
      page: $page,
      itemsPerPage: $itemsPerPage,
      filters: $filters,
      sorting: $sorting
      includeTitleNotes: $includeTitleNotes
    ) {
      deals { ${titlingExportDealsSnippet} }
    }
  }
`;

export const dealQuery = gql`
  query deal($id: ID!, $sources: [String]!, $types: [DealType]!) {
    deal(id: $id, sources: $sources, types: $types) {
      ${dealDetailSnippet}
      deal_products {
        id
        deal_id
        product_catalog_id
        price
        cost
        term
        coverage_type
        pen_session_id
        pen_rate_id
        pen_form_id
        product_catalog {
          id
          slug
          name
        }
      }
    }
  }
  ${ReferralSourceFragment}
`;

// MUTATIONS
export const estimateUpsert = gql`
  mutation estimateUpsert($deal: dealInput) {
    estimateUpsert(deal: $deal) {
      ${dealDetailSnippet}
    }
  }
  ${ReferralSourceFragment}
`;

export const customerInfoUpsert = gql`
  mutation customerInfoUpsert($deal: dealInput) {
    customerInfoUpsert(deal: $deal) {
      id
    }
  }
`;

export const creditAppAutomate = gql`
  mutation automateCreditApplication($deal_id: ID!) {
    automateCreditApplication(deal_id: $deal_id) {
      id
    }
  }
`;

export const dealUpdateStateAndSendEmails = gql`
  mutation dealUpdateStateAndSendEmails(
    $id: ID!
    $state: String!
    $date: DateTime
    $isR1ContractValidated: Boolean
    $isR1ContractUploaded: Boolean
    $dealIsSigningOnCom: Boolean
  ) {
    dealUpdateStateAndSendEmails(
      id: $id
      state: $state
      date: $date
      isR1ContractValidated: $isR1ContractValidated
      isR1ContractUploaded: $isR1ContractUploaded
      dealIsSigningOnCom: $dealIsSigningOnCom
    ) {
      id
      state
      signing_on_com
    }
  }
`;

export const resendEmails = gql`
  mutation resendEmails($dealId: ID!) {
    resendEmails(dealId: $dealId)
  }
`;

export const dealCompleted = gql`
  mutation dealCompleted($id: ID!, $state: String!) {
    dealCompleted(id: $id, state: $state) {
      id
    }
  }
`;

export const dealUnwound = gql`
  mutation dealUnwound($id: ID!, $boot_reason: bootReasonInput!) {
    dealUnwound(id: $id, boot_reason: $boot_reason) {
      id
    }
  }
`;

export const dealSeen = gql`
  mutation dealSeen($dealId: ID!) {
    dealSeen(dealId: $dealId)
  }
`;

export const dealInfoUpsert = gql`
  mutation dealInfoUpsert(
    $financialInfo: financialInfoInput
    $car: carInput
    $customer: customerInput
  ) {
    dealInfoUpsert(financialInfo: $financialInfo, car: $car, customer: $customer) {
      ${dealDetailSnippet}
    }
  }
  ${ReferralSourceFragment}
`;

export const acquisitionDealInfoUpsert = gql`
  mutation acquisitionDealInfoUpsert($deal: dealInput) {
    acquisitionDealInfoUpsert(deal: $deal) {
      id
    }
  }
`;

export const buyerCobuyerSwap = gql`
  mutation buyerCobuyerSwap($deal_id: ID!, $customer_id: ID!, $cobuyer_id: ID!) {
    buyerCobuyerSwap(deal_id: $deal_id, customer_id: $customer_id, cobuyer_id: $cobuyer_id) {
      id
    }
  }
`;

export const dealFinalizeAcquisition = gql`
  mutation dealFinalizeAcquisition(
    $id: ID!
    $address_line: String!
    $address_line_2: String
    $city: String!
    $state: String!
    $zip: String!
    $county: String!
    $dob: Date!
    $cash_to_customer: Float!
    $mileage: Float
    $vehicle_payoff: Float
  ) {
    dealFinalizeAcquisition(
      id: $id
      address_line: $address_line
      address_line_2: $address_line_2
      city: $city
      state: $state
      zip: $zip
      county: $county
      dob: $dob
      cash_to_customer: $cash_to_customer
      mileage: $mileage
      vehicle_payoff: $vehicle_payoff
    )
  }
`;

export const skipTimelineStep = gql`
  mutation skipTimelineStep($dealId: ID!, $documentProgressStatus: DocumentProgressStatus) {
    skipTimelineStep(dealId: $dealId, documentProgressStatus: $documentProgressStatus) {
      document_progress_status
      r1_contract_validation_errors_object {
        errors
      }
      r1_contract_validation_warnings_object {
        warnings
      }
      r1_contract_manually_validated
    }
  }
`;

export const createNewEstimateAndSetFollowUp = gql`
  mutation createNewEstimateAndSetFollowUp(
    $first_name: String!
    $last_name: String!
    $phone_number: String!
    $email: String
    $address_line: String
    $address_line_2: String
    $zip: String
    $city: String
    $state: String
    $county: String
    $date_utc: DateTime!
    $note: String
  ) {
    createNewEstimateAndSetFollowUp(
      first_name: $first_name
      last_name: $last_name
      phone_number: $phone_number
      email: $email
      address_line: $address_line
      address_line_2: $address_line_2
      zip: $zip
      city: $city
      state: $state
      county: $county
      date_utc: $date_utc
      note: $note
    ) {
      id
    }
  }
`;

export const onAICloser = gql`
  subscription onAICloser($dealId: ID!) {
    onAICloser(dealId: $dealId) {
      ${dealDetailSnippet}
    }
  }
  ${ReferralSourceFragment}
`;

export const onAICloserCallStatus = gql`
  subscription onAICloserCallStatus($dealId: ID!) {
    onAICloserCallStatus(dealId: $dealId) {
      event
      reason
      callSummary
      inVoicemail
      userSentiment
      callSuccessful
    }
  }
`;
